import { useCallback, useEffect, useState } from "react";
import type { Dispatch, SetStateAction } from "react";

export interface Loaded<T> {
  data: T | null;
  setData: Dispatch<SetStateAction<T | null>>;
  error: unknown;
  reload: () => void;
}

/** Loads data once (and on `reload`). Errors are returned, never thrown into render. */
export function useLoad<T>(load: () => Promise<T>): Loaded<T> {
  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [tick, setTick] = useState(0);
  useEffect(() => {
    let cancelled = false;
    load().then(
      (value) => {
        if (cancelled) return;
        setData(value);
        setError(null);
      },
      (err: unknown) => {
        if (!cancelled) setError(err);
      },
    );
    return () => {
      cancelled = true;
    };
    // `load` is a fresh closure each render by design; reload is driven by `tick`.
  }, [tick]);
  const reload = useCallback(() => setTick((n) => n + 1), []);
  return { data, setData, error, reload };
}
